import { useCallback, useMemo, useState, type ReactElement } from "react";
import { Trans, useLingui } from "@lingui/macro";
import {
  AlignLeft,
  BarChart3,
  Grid3x3,
  LineChart,
  ListTree,
  RefreshCw,
  Ruler,
  Table2,
  Waves,
  X,
} from "lucide-react";

import { WithheldCount } from "@app/components/WithheldCount";
import { cn } from "@app/lib/utils";
import type {
  CompositionResponse,
  PathwayDimension,
  Representation,
} from "@app/types/dashboard-pathway";
import DimensionPicker from "./DimensionPicker";
import { useRepresentationLabels } from "./labels";
import {
  AreaChart,
  CountTable,
  CurveChart,
  DistributionChart,
  FlowChart,
  MatrixChart,
  NestTree,
  RulerChart,
  StackChart,
  type RepresentationProps,
} from "./Representations";

/**
 * Card 1 — what this practice is made of.
 *
 * The card is a ZOOM LEVEL, not a fixed chart. Three gestures, deliberately distinct: adding a
 * column changes what is being ASKED, unfolding a row changes what is being LOOKED AT, and clicking
 * a label changes WHO is being talked about. Confusing the three is what makes a dashboard feel like
 * a maze.
 *
 * The representations offered come from the server, which derives them from the ontology's declared
 * types. The reader chooses among them and the choice is saved with the card — a card is a permanent
 * object, so a ruler pinned today is a ruler tomorrow morning.
 */

const REPRESENTATION_ICONS: Record<Representation, typeof BarChart3> = {
  empilement: AlignLeft,
  matrice: Grid3x3,
  regle: Ruler,
  emboitement: ListTree,
  flux: Waves,
  aires: BarChart3,
  courbe: LineChart,
  distribution: BarChart3,
  tableau: Table2,
};

const RENDERERS: Record<Representation, (props: RepresentationProps) => ReactElement> = {
  empilement: StackChart,
  matrice: MatrixChart,
  regle: RulerChart,
  emboitement: NestTree,
  flux: FlowChart,
  aires: AreaChart,
  courbe: CurveChart,
  distribution: DistributionChart,
  tableau: CountTable,
};

export interface CompositionCardProps {
  /** DOM anchor, so a question chip can bring the reader to the card it just reconfigured. */
  id: string;
  data: CompositionResponse | null;
  loading: boolean;
  error: string | null;
  title: string;
  dims: string[];
  mode: "n" | "pct";
  representation: Representation | null;
  maxDimensions: number;
  catalogue: PathwayDimension[];
  onChangeDims: (dims: string[]) => void;
  onChangeMode: (mode: "n" | "pct") => void;
  onChangeRepresentation: (representation: Representation | null) => void;
  onPick: (dimension: string, value: string) => void;
  onRemove?: () => void;
  /** Turn the current configuration into a pinned question chip. */
  onPin?: () => void;
}

export default function CompositionCard({
  id,
  data,
  loading,
  error,
  title,
  dims,
  mode,
  representation,
  maxDimensions,
  catalogue,
  onChangeDims,
  onChangeMode,
  onChangeRepresentation,
  onPick,
  onRemove,
  onPin,
}: CompositionCardProps) {
  const { t } = useLingui();
  const labels = useRepresentationLabels();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleExpand = useCallback((path: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  // The server's default when the reader has not chosen, and a fallback when a saved choice stops
  // applying — a card must not punish its reader for an ontology change they did not make.
  const active: Representation | null = useMemo(() => {
    if (!data) return null;
    if (representation && data.representations.includes(representation)) return representation;
    return data.default_representation;
  }, [data, representation]);

  const shownDims = data?.dimensions ?? [];
  const Renderer = active ? RENDERERS[active] : null;

  return (
    <section id={id} className="scroll-mt-24 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <header className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-2">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>

        <div className="flex flex-wrap items-center gap-1.5">
          {shownDims.map((dimension) => (
            <span
              key={dimension.key}
              className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-1 text-2xs font-medium text-primary"
            >
              {dimension.label}
              {shownDims.length > 1 && (
                <button
                  type="button"
                  aria-label={t`Retirer ${dimension.label}`}
                  onClick={() => onChangeDims(dims.filter((d) => d !== dimension.key))}
                  className="rounded-full p-0.5 hover:bg-primary/20"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              )}
            </span>
          ))}
          <DimensionPicker
            dimensions={catalogue}
            exclude={dims}
            disabled={dims.length >= maxDimensions}
            onPick={(key) => onChangeDims([...dims, key])}
          />
        </div>

        <div className="ml-auto flex items-center gap-2">
          {/* n / % — the same data asked two different questions. */}
          <div className="inline-flex items-center rounded-md bg-slate-100 p-0.5">
            {(["n", "pct"] as const).map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => onChangeMode(value)}
                aria-pressed={mode === value}
                className={cn(
                  "rounded px-2 py-0.5 text-2xs font-medium transition-colors",
                  mode === value ? "bg-white text-primary shadow-sm" : "text-slate-500 hover:text-slate-700",
                )}
              >
                {value === "n" ? t`n` : t`%`}
              </button>
            ))}
          </div>

          {/* Only the forms these data types admit — and the counted table is always among them, so
              the card can never refuse the question. */}
          {data && data.representations.length > 1 && (
            <div className="inline-flex items-center gap-0.5 rounded-md bg-slate-100 p-0.5">
              {data.representations.map((option) => {
                const Icon = REPRESENTATION_ICONS[option];
                return (
                  <button
                    key={option}
                    type="button"
                    title={labels[option]}
                    aria-label={labels[option]}
                    aria-pressed={active === option}
                    onClick={() => onChangeRepresentation(option)}
                    className={cn(
                      "rounded p-1 transition-colors",
                      active === option
                        ? "bg-white text-primary shadow-sm"
                        : "text-slate-400 hover:text-slate-600",
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                  </button>
                );
              })}
            </div>
          )}

          {onPin && (
            <button
              type="button"
              onClick={onPin}
              className="text-2xs font-medium text-slate-500 hover:text-primary"
            >
              <Trans>Épingler</Trans>
            </button>
          )}
          {onRemove && (
            <button
              type="button"
              aria-label={t`Retirer la carte`}
              onClick={onRemove}
              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </header>

      {loading && !data && (
        <div className="flex h-40 items-center justify-center gap-2 text-sm text-slate-400">
          <RefreshCw className="h-3.5 w-3.5 animate-spin" />
          <Trans>Chargement...</Trans>
        </div>
      )}

      {error && !data && (
        <p className="py-10 text-center text-sm text-slate-400">
          <Trans>Cette vue n'est pas disponible sur ce périmètre.</Trans>
        </p>
      )}

      {data && Renderer && data.nodes.length > 0 && (
        <Renderer
          data={data}
          mode={mode}
          onPick={onPick}
          expanded={expanded}
          onToggleExpand={toggleExpand}
        />
      )}

      {/* Named, not generic. "No data" makes the reader doubt the board; "no episode of this
          selection states a gesture" tells them which field to go and document, or which filter to
          lift. A `null` count is withheld (under the anonymity threshold), never zero. */}
      {data && data.nodes.length === 0 && (
        <p className="py-10 text-center text-sm text-slate-400">
          {data.undocumented.episodes !== 0 ? (
            <Trans>
              Aucun des {data.total.episodes ?? <WithheldCount />} épisodes de cette sélection ne renseigne «{" "}
              {data.dimensions[0]?.label} ».
            </Trans>
          ) : (
            <Trans>Aucun épisode dans cette sélection.</Trans>
          )}
        </p>
      )}

      {data && (
        <footer className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-slate-100 pt-2 text-2xs text-slate-500">
          <span className="tabular-nums">
            <Trans>
              {data.total.episodes ?? <WithheldCount />} épisodes · {data.total.patients ?? <WithheldCount />} patients
            </Trans>
          </span>
          {/* Counted aside, never drawn: a 30 %-wide "not documented" band is the hole in the record
              rendered as if it were a kind of care. */}
          {data.undocumented.episodes !== 0 && (
            <span className="tabular-nums">
              <Trans>
                {data.undocumented.episodes ?? <WithheldCount />} épisodes hors composition (non renseignés)
              </Trans>
            </span>
          )}
          {data.overlapping && (
            <span>
              <Trans>Un épisode peut porter plusieurs valeurs : les segments dépassent le total.</Trans>
            </span>
          )}
        </footer>
      )}
    </section>
  );
}
